import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

export interface ThemeConfig {
  name: string;
  displayName: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    button: {
      primary: string;
      secondary: string;
      hover: string;
    };
    card: {
      background: string;
      shadow: string;
    };
    header: {
      background: string;
      text: string;
    };
  };
  typography: {
    fontFamily: string;
    fontSize: {
      small: string;
      medium: string;
      large: string;
      xlarge: string;
    };
    fontWeight: {
      normal: number;
      medium: number;
      bold: number;
    };
  };
  spacing: {
    small: string;
    medium: string;
    large: string;
    xlarge: string;
  };
  borderRadius: {
    small: string;
    medium: string;
    large: string;
  };
}

export const THEMES: Record<string, ThemeConfig> = {
  moderno: {
    name: 'moderno',
    displayName: 'Moderno',
    colors: {
      primary: '#2563eb',
      secondary: '#1e40af',
      accent: '#3b82f6',
      background: '#f8fafc',
      surface: '#ffffff',
      text: '#1e293b',
      textSecondary: '#64748b',
      border: '#e2e8f0',
      button: {
        primary: '#2563eb',
        secondary: '#64748b',
        hover: '#1d4ed8'
      },
      card: {
        background: '#ffffff',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      header: {
        background: '#ffffff',
        text: '#1e293b'
      }
    },
    typography: {
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      fontSize: {
        small: '14px',
        medium: '16px',
        large: '18px',
        xlarge: '24px'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 600
      }
    },
    spacing: {
      small: '8px',
      medium: '16px',
      large: '24px',
      xlarge: '32px'
    },
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '12px'
    }
  },
  corporativo: {
    name: 'corporativo',
    displayName: 'Corporativo',
    colors: {
      primary: '#000d2a',
      secondary: '#006b70',
      accent: '#008b4f',
      background: '#f5f7fa',
      surface: '#ffffff',
      text: '#000d2a',
      textSecondary: '#6b7280',
      border: '#d1d5db',
      button: {
        primary: '#000d2a',
        secondary: '#6b7280',
        hover: '#001a4d'
      },
      card: {
        background: '#ffffff',
        shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
      },
      header: {
        background: '#ffffff',
        text: '#000d2a'
      }
    },
    typography: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: {
        small: '13px',
        medium: '15px',
        large: '17px',
        xlarge: '22px'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 700
      }
    },
    spacing: {
      small: '6px',
      medium: '12px',
      large: '20px',
      xlarge: '28px'
    },
    borderRadius: {
      small: '2px',
      medium: '4px',
      large: '6px'
    }
  },
  creativo: {
    name: 'creativo',
    displayName: 'Creativo',
    colors: {
      primary: '#7c3aed',
      secondary: '#a855f7',
      accent: '#01f5ff',
      background: '#fef7ff',
      surface: '#ffffff',
      text: '#1f2937',
      textSecondary: '#6b7280',
      border: '#e879f9',
      button: {
        primary: '#7c3aed',
        secondary: '#a855f7',
        hover: '#6d28d9'
      },
      card: {
        background: '#ffffff',
        shadow: '0 10px 15px -3px rgba(139, 92, 246, 0.1), 0 4px 6px -2px rgba(139, 92, 246, 0.05)'
      },
      header: {
        background: '#ffffff',
        text: '#1f2937'
      }
    },
    typography: {
      fontFamily: 'Poppins, sans-serif',
      fontSize: {
        small: '14px',
        medium: '16px',
        large: '20px',
        xlarge: '28px'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 700
      }
    },
    spacing: {
      small: '10px',
      medium: '20px',
      large: '30px',
      xlarge: '40px'
    },
    borderRadius: {
      small: '8px',
      medium: '16px',
      large: '24px'
    }
  },
  minimalista: {
    name: 'minimalista',
    displayName: 'Minimalista',
    colors: {
      primary: '#374151',
      secondary: '#6b7280',
      accent: '#9ca3af',
      background: '#ffffff',
      surface: '#ffffff',
      text: '#111827',
      textSecondary: '#6b7280',
      border: '#e5e7eb',
      button: {
        primary: '#374151',
        secondary: '#6b7280',
        hover: '#1f2937'
      },
      card: {
        background: '#ffffff',
        shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
      },
      header: {
        background: '#ffffff',
        text: '#111827'
      }
    },
    typography: {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: {
        small: '12px',
        medium: '14px',
        large: '16px',
        xlarge: '20px'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 600
      }
    },
    spacing: {
      small: '4px',
      medium: '8px',
      large: '16px',
      xlarge: '24px'
    },
    borderRadius: {
      small: '2px',
      medium: '4px',
      large: '6px'
    }
  },
  personalizado: {
    name: 'personalizado',
    displayName: 'Personalizado',
    colors: {
      primary: '#6366f1',
      secondary: '#8b5cf6',
      accent: '#06b6d4',
      background: '#ffffff',
      surface: '#ffffff',
      text: '#1f2937',
      textSecondary: '#6b7280',
      border: '#e5e7eb',
      button: {
        primary: '#6366f1',
        secondary: '#8b5cf6',
        hover: '#4f46e5'
      },
      card: {
        background: '#ffffff',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      header: {
        background: '#ffffff',
        text: '#1f2937'
      }
    },
    typography: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: {
        small: '14px',
        medium: '16px',
        large: '18px',
        xlarge: '24px'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 600
      }
    },
    spacing: {
      small: '8px',
      medium: '16px',
      large: '24px',
      xlarge: '32px'
    },
    borderRadius: {
      small: '6px',
      medium: '8px',
      large: '12px'
    }
  }
};

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<string>('moderno');
  public currentTheme$ = this.currentThemeSubject.asObservable();

  private themes = THEMES;

  constructor(
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.initializeTheme();
  }

  public initializeTheme(): void {
    // Cargar tema personalizado si existe
    const savedCustomTheme = localStorage.getItem('customTheme');
    if (savedCustomTheme) {
      try {
        const customTheme = JSON.parse(savedCustomTheme);
        this.themes['personalizado'] = customTheme;
      } catch (error) {
        console.error('Error loading custom theme:', error);
      }
    }

    // Verificar si hay un tema en localStorage
    const savedTheme = localStorage.getItem('selectedTheme');
    if (savedTheme && this.themes[savedTheme]) {
      this.setTheme(savedTheme);
      return;
    }

    // Verificar query params en la URL
    this.route.queryParams.subscribe(params => {
      if (params['theme'] && this.themes[params['theme']]) {
        this.setTheme(params['theme']);
      }
    });
  }

  public getCurrentTheme(): string {
    return this.currentThemeSubject.value;
  }

  public getCurrentThemeConfig(): ThemeConfig {
    return this.themes[this.getCurrentTheme()];
  }

  public getAllThemes(): ThemeConfig[] {
    const themes = Object.values(this.themes);
    
    // Agregar tema personalizado si existe
    const customTheme = this.themes['personalizado'];
    if (customTheme && !themes.includes(customTheme)) {
      themes.push(customTheme);
    }
    
    return themes;
  }

  public setTheme(themeName: string): void {
    if (this.themes[themeName]) {
      this.currentThemeSubject.next(themeName);
      this.applyTheme(this.themes[themeName]);
      localStorage.setItem('selectedTheme', themeName);
      
      // Si es el tema personalizado, también guardar la configuración
      if (themeName === 'personalizado') {
        const customColors = localStorage.getItem('customThemeColors');
        if (customColors) {
          localStorage.setItem('customThemeColors', customColors);
        }
      }
    }
  }

  public setThemeFromQuery(themeName: string): void {
    if (this.themes[themeName]) {
      this.setTheme(themeName);
      // Actualizar la URL sin recargar la página
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { theme: themeName },
        queryParamsHandling: 'merge'
      });
    }
  }

  private applyTheme(theme: ThemeConfig): void {
    const root = document.documentElement;
    
    // Aplicar colores CSS variables
    root.style.setProperty('--theme-primary', theme.colors.primary);
    root.style.setProperty('--theme-secondary', theme.colors.secondary);
    root.style.setProperty('--theme-accent', theme.colors.accent);
    root.style.setProperty('--theme-background', theme.colors.background);
    root.style.setProperty('--theme-surface', theme.colors.surface);
    root.style.setProperty('--theme-text', theme.colors.text);
    root.style.setProperty('--theme-text-secondary', theme.colors.textSecondary);
    root.style.setProperty('--theme-border', theme.colors.border);
    root.style.setProperty('--theme-button-primary', theme.colors.button.primary);
    root.style.setProperty('--theme-button-secondary', theme.colors.button.secondary);
    root.style.setProperty('--theme-button-hover', theme.colors.button.hover);
    root.style.setProperty('--theme-card-background', theme.colors.card.background);
    root.style.setProperty('--theme-card-shadow', theme.colors.card.shadow);
    root.style.setProperty('--theme-header-background', theme.colors.header.background);
    root.style.setProperty('--theme-header-text', theme.colors.header.text);

    // Aplicar tipografía
    root.style.setProperty('--theme-font-family', theme.typography.fontFamily);
    root.style.setProperty('--theme-font-size-small', theme.typography.fontSize.small);
    root.style.setProperty('--theme-font-size-medium', theme.typography.fontSize.medium);
    root.style.setProperty('--theme-font-size-large', theme.typography.fontSize.large);
    root.style.setProperty('--theme-font-size-xlarge', theme.typography.fontSize.xlarge);
    root.style.setProperty('--theme-font-weight-normal', theme.typography.fontWeight.normal.toString());
    root.style.setProperty('--theme-font-weight-medium', theme.typography.fontWeight.medium.toString());
    root.style.setProperty('--theme-font-weight-bold', theme.typography.fontWeight.bold.toString());

    // Aplicar espaciado
    root.style.setProperty('--theme-spacing-small', theme.spacing.small);
    root.style.setProperty('--theme-spacing-medium', theme.spacing.medium);
    root.style.setProperty('--theme-spacing-large', theme.spacing.large);
    root.style.setProperty('--theme-spacing-xlarge', theme.spacing.xlarge);

    // Aplicar border radius
    root.style.setProperty('--theme-border-radius-small', theme.borderRadius.small);
    root.style.setProperty('--theme-border-radius-medium', theme.borderRadius.medium);
    root.style.setProperty('--theme-border-radius-large', theme.borderRadius.large);

    // Aplicar clase CSS al body para el tema
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${theme.name}`);
    
    // Debug: Verificar que las variables se aplicaron
    console.log(`🎨 Tema aplicado: ${theme.name}`);
    console.log('Variables CSS aplicadas:', {
      primary: theme.colors.primary,
      buttonPrimary: theme.colors.button.primary,
      headerBackground: theme.colors.header.background
    });
  }

  public getThemeByName(name: string): ThemeConfig | undefined {
    return this.themes[name];
  }

  public createCustomTheme(colors: {
    primary: string;
    secondary?: string;
    accent?: string;
    background?: string;
    surface?: string;
    text?: string;
    textSecondary?: string;
    border?: string;
  }): ThemeConfig {
    const baseTheme = this.themes['personalizado'];
    
    return {
      ...baseTheme,
      colors: {
        ...baseTheme.colors,
        primary: colors.primary,
        secondary: colors.secondary || this.lightenColor(colors.primary, 20),
        accent: colors.accent || this.generateAccentColor(colors.primary),
        background: colors.background || '#ffffff',
        surface: colors.surface || '#ffffff',
        text: colors.text || '#1f2937',
        textSecondary: colors.textSecondary || '#6b7280',
        border: colors.border || '#e5e7eb',
        button: {
          primary: colors.primary,
          secondary: colors.secondary || this.lightenColor(colors.primary, 20),
          hover: this.darkenColor(colors.primary, 10)
        }
      }
    };
  }

  public setCustomTheme(colors: {
    primary: string;
    secondary?: string;
    accent?: string;
    background?: string;
    surface?: string;
    text?: string;
    textSecondary?: string;
    border?: string;
  }): void {
    const customTheme = this.createCustomTheme(colors);
    this.themes['personalizado'] = customTheme;
    
    // Guardar el tema personalizado en localStorage
    localStorage.setItem('customTheme', JSON.stringify(customTheme));
    localStorage.setItem('customThemeColors', JSON.stringify(colors));
    
    // Aplicar el tema personalizado
    this.setTheme('personalizado');
  }

  private lightenColor(color: string, percent: number): string {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  private darkenColor(color: string, percent: number): string {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return "#" + (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
      (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
      (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1);
  }

  private generateAccentColor(primaryColor: string): string {
    // Generar un color de acento complementario
    const hue = this.getHueFromHex(primaryColor);
    const complementaryHue = (hue + 180) % 360;
    return this.hslToHex(complementaryHue, 70, 50);
  }

  private getHueFromHex(hex: string): number {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;

    if (max === min) {
      h = 0;
    } else if (max === r) {
      h = ((g - b) / (max - min)) % 6;
    } else if (max === g) {
      h = (b - r) / (max - min) + 2;
    } else {
      h = (r - g) / (max - min) + 4;
    }

    return Math.round(h * 60);
  }

  private hslToHex(h: number, s: number, l: number): string {
    h = h / 360;
    s = s / 100;
    l = l / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h * 6) % 2 - 1));
    const m = l - c / 2;

    let r, g, b;

    if (h < 1/6) {
      r = c; g = x; b = 0;
    } else if (h < 2/6) {
      r = x; g = c; b = 0;
    } else if (h < 3/6) {
      r = 0; g = c; b = x;
    } else if (h < 4/6) {
      r = 0; g = x; b = c;
    } else if (h < 5/6) {
      r = x; g = 0; b = c;
    } else {
      r = c; g = 0; b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }
}
